import React from 'react';
import { Reveal } from './Reveal';
import { EvidenceChip, EvidenceState } from '../ui/EvidenceChip';

interface ShowcaseCard {
  level: 'strong' | 'moderate';
  score: number;
  levelLabel: string;
  ref: string;
  title: string;
  meta: React.ReactNode;
  chips: { state: EvidenceState; text: string }[];
  view: 'primary' | 'outline';
  dim?: boolean;
}

const CARDS: ShowcaseCard[] = [
  {
    level: 'strong',
    score: 72,
    levelLabel: 'STRONG',
    ref: 'U006',
    title: 'Physical Therapist Training',
    meta: (
      <>
        VA · NAICS 611430 · <span className="text-urgent font-semibold">⏱ closes in 4 days</span>
      </>
    ),
    chips: [
      { state: 'grounded', text: 'NAICS match' },
      { state: 'inferred', text: 'SDVOSB set-aside' },
    ],
    view: 'primary',
  },
  {
    level: 'moderate',
    score: 58,
    levelLabel: 'MODERATE',
    ref: 'D-04',
    title: 'IT Modernization Support',
    meta: <>Interior · NAICS 541519 · closes in 22 days</>,
    chips: [
      { state: 'grounded', text: 'NAICS match' },
      { state: 'gap', text: 'No agency history' },
    ],
    view: 'outline',
    dim: true,
  },
];

/** Section 05 — the navy product band with a dashboard browser mockup. */
export function ProductShowcase() {
  return (
    <section id="product" className="bg-bw-sidebar scroll-mt-20">
      <div className="max-w-[1180px] mx-auto px-5 sm:px-8 py-24">
        <Reveal className="text-center max-w-[680px] mx-auto mb-12">
          <div className="font-mono text-[11px] font-semibold tracking-[0.14em] text-bw-accent mb-4">
            05 — THE PRODUCT
          </div>
          <h2 className="text-[clamp(30px,3.6vw,46px)] leading-[1.08] font-extrabold tracking-[-0.03em] text-white mb-4">
            What’s worth your time today.
          </h2>
          <p className="text-lg leading-[1.6] text-white/70">
            The dashboard leads with your best fits, soonest closing — each card justified at a glance.
          </p>
        </Reveal>

        <Reveal className="max-w-[860px] mx-auto">
          <div className="bg-bw-app rounded-2xl shadow-panel overflow-hidden">
            {/* window chrome */}
            <div className="flex items-center gap-2 px-[18px] py-3 bg-bw-sidebar">
              <span className="w-[11px] h-[11px] rounded-full bg-bw-accent inline-block" />
              <span className="w-[11px] h-[11px] rounded-full bg-fit-moderate-stroke inline-block" />
              <span className="w-[11px] h-[11px] rounded-full bg-fit-strong-stroke inline-block" />
              <span className="ml-3.5 font-mono text-[11px] text-white/60">
                bidwin.servetdigital.com/dashboard
              </span>
            </div>

            <div className="p-5 sm:p-7">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
                <div>
                  <div className="text-xl font-extrabold tracking-tight text-bw-sidebar">
                    Best fits for you
                  </div>
                  <div className="font-mono text-[11.5px] text-bw-muted mt-1">
                    301 OPEN · 4 CLOSING SOON · 2 DRAFTS
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs font-semibold text-bw-primary bg-bw-primary-tint border border-bw-border px-3.5 py-2 rounded-lg font-mono">
                  ⌘K&nbsp;&nbsp;SEARCH
                </div>
              </div>

              <div className="flex flex-col gap-3">
                {CARDS.map((c) => (
                  <div
                    key={c.ref}
                    className={`bg-bw-surface border border-bw-border rounded-xl p-[18px] shadow-card ${c.dim ? 'opacity-95' : ''}`}
                  >
                    <div className="flex items-start gap-3.5">
                      <div
                        className={`w-[52px] h-[52px] rounded-[11px] flex flex-col items-center justify-center flex-none ${
                          c.level === 'strong'
                            ? 'bg-fit-strong-bg border border-fit-strong-border'
                            : 'bg-fit-moderate-bg border border-fit-moderate-border'
                        }`}
                      >
                        <span
                          className={`font-mono text-[19px] font-semibold leading-none ${
                            c.level === 'strong' ? 'text-fit-strong' : 'text-fit-moderate'
                          }`}
                        >
                          {c.score}
                        </span>
                        <span
                          className={`text-[8px] font-bold ${
                            c.level === 'strong' ? 'text-fit-strong' : 'text-fit-moderate'
                          }`}
                        >
                          {c.levelLabel}
                        </span>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2.5 flex-wrap">
                          <span className="font-mono text-[11px] text-bw-muted">{c.ref}</span>
                          <span className="text-[15.5px] font-bold text-bw-ink">{c.title}</span>
                        </div>
                        <div className="font-mono text-[11.5px] text-bw-muted my-1.5">{c.meta}</div>
                        <div className="flex gap-2 flex-wrap">
                          {c.chips.map((chip) => (
                            <EvidenceChip key={chip.text} state={chip.state}>
                              {chip.text}
                            </EvidenceChip>
                          ))}
                        </div>
                      </div>

                      <span
                        className={`text-[13px] font-semibold px-3.5 py-2 rounded-lg flex-none self-center ${
                          c.view === 'primary'
                            ? 'text-white bg-bw-primary'
                            : 'text-bw-primary bg-bw-surface border border-bw-border'
                        }`}
                      >
                        View
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
